import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, Play, Trash2, Users, Plus, Settings, Edit, Palette, BookPlus, UserPlus, Search } from "lucide-react";
import { AddVideoToCourseDialog } from "@/components/video/AddVideoToCourseDialog";

interface VideoCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_default: boolean;
}

interface Video {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string;
  duration_seconds?: number;
  category: string;
  tags: string[];
  upload_type: 'admin' | 'teacher';
  uploaded_by: string;
  district_id?: string;
  is_public: boolean;
  status: string;
  created_at: string;
  uploader_name?: string;
  assignment_count?: number;
}

interface VideoAssignment {
  id: string;
  video_id: string;
  teacher_id: string;
  assigned_by: string;
  assigned_at: string;
  notes?: string;
  video: Video;
  teacher_name?: string;
}

export default function VideoLibrary() {
  const { user } = useAuth();
  const { isAdmin, isMentor } = useRole();
  const { toast } = useToast();
  
  const canAssignVideos = isAdmin || isMentor;
  
  const [videos, setVideos] = useState<Video[]>([]);
  const [videoCategories, setVideoCategories] = useState<VideoCategory[]>([]);
  const [assignments, setAssignments] = useState<VideoAssignment[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showAddToCourseDialog, setShowAddToCourseDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [editingCategory, setEditingCategory] = useState<VideoCategory | null>(null);

  const [newVideo, setNewVideo] = useState({
    title: "",
    description: "",
    category: "",
    tags: "",
    is_public: false,
    video_file: null as File | null
  });

  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    color: "#3B82F6"
  });

  const [assignmentData, setAssignmentData] = useState({
    teacher_ids: [] as string[],
    notes: ""
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, isAdmin]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadVideos(),
      loadCategories(),
      isAdmin && loadAssignments(),
      isAdmin && loadTeachers()
    ].filter(Boolean));
    setLoading(false);
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('video_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setVideoCategories(data || []);
      
      // Set default category if none selected
      if (!newVideo.category && data && data.length > 0) {
        setNewVideo(prev => ({ ...prev, category: data[0].name.toLowerCase() }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get uploader names and assignment counts
      const videosWithNames = await Promise.all(
        (data || []).map(async (video) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', video.uploaded_by)
            .single();
          
          // Get assignment count
          const { count } = await supabase
            .from('video_assignments')
            .select('*', { count: 'exact', head: true })
            .eq('video_id', video.id);
          
          return {
            ...video,
            upload_type: video.upload_type as 'admin' | 'teacher',
            uploader_name: profile 
              ? `${profile.first_name} ${profile.last_name}`
              : 'Unknown',
            assignment_count: count || 0
          };
        })
      );

      setVideos(videosWithNames);
    } catch (error) {
      console.error('Error loading videos:', error);
      toast({
        title: "Error",
        description: "Failed to load videos",
        variant: "destructive"
      });
    }
  };

  const loadAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('video_assignments')
        .select('*, video:videos(*)')
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      // Get teacher names separately  
      const assignmentsWithNames = await Promise.all(
        (data || []).map(async (assignment) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', assignment.teacher_id)
            .single();
          
          return {
            ...assignment,
            video: {
              ...assignment.video,
              upload_type: assignment.video.upload_type as 'admin' | 'teacher'
            },
            teacher_name: profile 
              ? `${profile.first_name} ${profile.last_name}`
              : 'Unknown'
          };
        })
      );

      setAssignments(assignmentsWithNames);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const loadTeachers = async () => {
    try {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['user', 'instructor', 'mentor'])
        .eq('is_active', true);

      if (error) throw error;

      const userIds = roles?.map(role => role.user_id) || [];
      
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);

        if (profilesError) throw profilesError;
        setTeachers(profiles || []);
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const handleUploadVideo = async () => {
    if (!newVideo.title || !newVideo.video_file) {
      toast({
        title: "Error",
        description: "Please provide a title and select a video file",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      // Upload video file to storage
      const fileExt = newVideo.video_file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(fileName, newVideo.video_file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('videos')
        .getPublicUrl(fileName);

      // Save video record
      const { error: insertError } = await supabase
        .from('videos')
        .insert({
          title: newVideo.title,
          description: newVideo.description,
          video_url: publicUrl,
          category: newVideo.category,
          tags: newVideo.tags.split(',').map(t => t.trim()).filter(Boolean),
          upload_type: isAdmin ? 'admin' : 'teacher',
          uploaded_by: user?.id,
          is_public: newVideo.is_public
        });

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "Video uploaded successfully"
      });

      setShowUploadDialog(false);
      setNewVideo({
        title: "",
        description: "",
        category: videoCategories.length > 0 ? videoCategories[0].name.toLowerCase() : "",
        tags: "",
        is_public: false,
        video_file: null
      });
      loadVideos();
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({
        title: "Error",
        description: "Failed to upload video",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleAssignVideo = async () => {
    if (!selectedVideo || assignmentData.teacher_ids.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one teacher",
        variant: "destructive"
      });
      return;
    }

    try {
      const assignments = assignmentData.teacher_ids.map(teacherId => ({
        video_id: selectedVideo.id,
        teacher_id: teacherId,
        assigned_by: user?.id,
        notes: assignmentData.notes
      }));

      const { error } = await supabase
        .from('video_assignments')
        .insert(assignments);

      if (error) throw error;

      toast({
        title: "Success!",
        description: `Video assigned to ${assignmentData.teacher_ids.length} teacher(s)`,
      });

      setShowAssignDialog(false);
      setAssignmentData({ teacher_ids: [], notes: "" });
      setTeacherSearchTerm("");
      setSelectedVideo(null);
      loadAssignments();
      loadVideos(); // Refresh to update assignment counts
    } catch (error) {
      console.error('Error assigning video:', error);
      toast({
        title: "Error",
        description: "Failed to assign video",
        variant: "destructive"
      });
    }
  };

  const toggleAllTeachers = (checked: boolean) => {
    if (checked) {
      const filteredTeacherIds = teachers
        .filter(t => {
          const searchLower = teacherSearchTerm.toLowerCase();
          const matchesSearch = 
            `${t.first_name} ${t.last_name}`.toLowerCase().includes(searchLower) ||
            t.email.toLowerCase().includes(searchLower);
          return matchesSearch;
        })
        .map(t => t.id);
      setAssignmentData({ ...assignmentData, teacher_ids: filteredTeacherIds });
    } else {
      setAssignmentData({ ...assignmentData, teacher_ids: [] });
    }
  };

  const filteredTeachers = teachers.filter(teacher => {
    const searchLower = teacherSearchTerm.toLowerCase();
    return (
      `${teacher.first_name} ${teacher.last_name}`.toLowerCase().includes(searchLower) ||
      teacher.email.toLowerCase().includes(searchLower)
    );
  });

  const handleDeleteVideo = async (videoId: string) => {
    if (!confirm('Are you sure you want to delete this video?')) return;

    try {
      const { error } = await supabase
        .from('videos')
        .update({ status: 'deleted' })
        .eq('id', videoId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Video deleted successfully"
      });
      loadVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: "Error",
        description: "Failed to delete video",
        variant: "destructive"
      });
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a category name",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('video_categories')
        .insert({
          name: newCategory.name,
          description: newCategory.description,
          color: newCategory.color,
          created_by: user?.id
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category created successfully"
      });

      setShowCategoryDialog(false);
      setNewCategory({ name: "", description: "", color: "#3B82F6" });
      loadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Error",
        description: "Failed to create category",
        variant: "destructive"
      });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !newCategory.name.trim()) return;

    try {
      const { error } = await supabase
        .from('video_categories')
        .update({
          name: newCategory.name,
          description: newCategory.description,
          color: newCategory.color
        })
        .eq('id', editingCategory.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category updated successfully"
      });

      setShowCategoryDialog(false);
      setEditingCategory(null);
      setNewCategory({ name: "", description: "", color: "#3B82F6" });
      loadCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category? Videos using this category will need to be recategorized.')) return;

    try {
      const { error } = await supabase
        .from('video_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category deleted successfully"
      });

      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive"
      });
    }
  };

  const openEditCategoryDialog = (category: VideoCategory) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      description: category.description || "",
      color: category.color
    });
    setShowCategoryDialog(true);
  };

  const filteredVideos = videos.filter(video => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = video.title.toLowerCase().includes(searchLower) ||
                         video.description?.toLowerCase().includes(searchLower) ||
                         video.tags?.some(tag => tag.toLowerCase().includes(searchLower));
    const matchesCategory = categoryFilter === "all" || video.category.toLowerCase() === categoryFilter.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Video Library</h1>
          <p className="text-muted-foreground">
            {isAdmin ? "Manage and assign videos to teachers" : "Access your video library"}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Categories
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Manage Video Categories'}
                  </DialogTitle>
                </DialogHeader>
                
                {!editingCategory ? (
                  <div className="space-y-6">
                    {/* Add New Category Form */}
                    <div className="space-y-4 p-4 border rounded-lg">
                      <h3 className="font-semibold">Add New Category</h3>
                      <div className="grid gap-4">
                        <div>
                          <Label htmlFor="category-name">Category Name</Label>
                          <Input
                            id="category-name"
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                            placeholder="Enter category name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="category-description">Description</Label>
                          <Textarea
                            id="category-description"
                            value={newCategory.description}
                            onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                            placeholder="Enter category description"
                          />
                        </div>
                        <div>
                          <Label htmlFor="category-color">Color</Label>
                          <div className="flex gap-2 items-center">
                            <Input
                              id="category-color"
                              type="color"
                              value={newCategory.color}
                              onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                              className="w-16 h-10"
                            />
                            <span className="text-sm text-muted-foreground">{newCategory.color}</span>
                          </div>
                        </div>
                        <Button onClick={handleCreateCategory}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Category
                        </Button>
                      </div>
                    </div>

                    {/* Existing Categories */}
                    <div className="space-y-2">
                      <h3 className="font-semibold">Existing Categories</h3>
                      <div className="grid gap-2">
                        {videoCategories.map((category) => (
                          <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: category.color }}
                              />
                              <div>
                                <div className="font-medium">{category.name}</div>
                                {category.description && (
                                  <div className="text-sm text-muted-foreground">{category.description}</div>
                                )}
                              </div>
                              {category.is_default && (
                                <Badge variant="secondary" className="text-xs">Default</Badge>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditCategoryDialog(category)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {!category.is_default && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCategory(category.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-category-name">Category Name</Label>
                      <Input
                        id="edit-category-name"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-category-description">Description</Label>
                      <Textarea
                        id="edit-category-description"
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        placeholder="Enter category description"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-category-color">Color</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id="edit-category-color"
                          type="color"
                          value={newCategory.color}
                          onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                          className="w-16 h-10"
                        />
                        <span className="text-sm text-muted-foreground">{newCategory.color}</span>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => {
                        setEditingCategory(null);
                        setNewCategory({ name: "", description: "", color: "#3B82F6" });
                      }}>
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateCategory}>
                        Update Category
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button>
                <Upload className="h-4 w-4 mr-2" />
                Upload Video
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Upload New Video</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                    placeholder="Enter video title"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                    placeholder="Enter video description"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={newVideo.category} onValueChange={(value) => setNewVideo({...newVideo, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border shadow-lg z-50">
                      {videoCategories.map(category => (
                        <SelectItem key={category.id} value={category.name.toLowerCase()}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: category.color }}
                            />
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tags">Keywords/Tags</Label>
                  <Input
                    id="tags"
                    value={newVideo.tags}
                    onChange={(e) => setNewVideo({...newVideo, tags: e.target.value})}
                    placeholder="coaching, classroom management, assessment..."
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Add keywords to make this video easy to find (separate with commas)
                  </p>
                </div>
                <div>
                  <Label htmlFor="video-file">Video File</Label>
                  <Input
                    id="video-file"
                    type="file"
                    accept="video/*"
                    onChange={(e) => setNewVideo({...newVideo, video_file: e.target.files?.[0] || null})}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUploadVideo} disabled={uploading}>
                    {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Upload
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Input
            placeholder="Search videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-lg z-50">
            <SelectItem value="all">All Categories</SelectItem>
            {videoCategories.map(category => (
              <SelectItem key={category.id} value={category.name.toLowerCase()}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="videos" className="w-full">
        <TabsList>
          <TabsTrigger value="videos">All Videos</TabsTrigger>
          <TabsTrigger value="my-videos">My Videos</TabsTrigger>
          {isAdmin && <TabsTrigger value="assignments">Assignments</TabsTrigger>}
        </TabsList>

        <TabsContent value="videos" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <Card key={video.id} className="group hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg line-clamp-2">{video.title}</CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">
                        {video.description}
                      </CardDescription>
                    </div>
                    {(isAdmin || video.uploaded_by === user?.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteVideo(video.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    <Badge 
                      variant="secondary" 
                      style={{ 
                        backgroundColor: videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color + '20',
                        color: videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color,
                        border: `1px solid ${videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color}40`
                      }}
                    >
                      {video.category}
                    </Badge>
                    {video.is_public && <Badge variant="outline">Public</Badge>}
                    <Badge variant="outline">{video.upload_type}</Badge>
                  </div>
                  {video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {video.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Uploaded by {video.uploader_name}
                    </span>
                    {canAssignVideos && video.assignment_count && video.assignment_count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {video.assignment_count} assigned
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1">
                      <Play className="h-4 w-4 mr-2" />
                      Play
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedVideo(video);
                        setShowAddToCourseDialog(true);
                      }}
                      title="Add to course"
                    >
                      <BookPlus className="h-4 w-4" />
                    </Button>
                    {canAssignVideos && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => {
                          setSelectedVideo(video);
                          setShowAssignDialog(true);
                        }}
                        className="gap-1"
                      >
                        <UserPlus className="h-4 w-4" />
                        Assign
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="my-videos" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.filter(v => v.uploaded_by === user?.id).map((video) => (
              <Card key={video.id} className="group hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg line-clamp-2">{video.title}</CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">
                        {video.description}
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteVideo(video.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    <Badge 
                      variant="secondary"
                      style={{ 
                        backgroundColor: videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color + '20',
                        color: videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color,
                        border: `1px solid ${videoCategories.find(c => c.name.toLowerCase() === video.category.toLowerCase())?.color}40`
                      }}
                    >
                      {video.category}
                    </Badge>
                    {video.is_public && <Badge variant="outline">Public</Badge>}
                  </div>
                  {video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {video.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1">
                      <Play className="h-4 w-4 mr-2" />
                      Play
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedVideo(video);
                        setShowAddToCourseDialog(true);
                      }}
                      title="Add to course"
                    >
                      <BookPlus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="assignments" className="space-y-4">
            <div className="space-y-4">
              {assignments.map((assignment) => (
                <Card key={assignment.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{assignment.video.title}</CardTitle>
                        <CardDescription>
                          Assigned to {assignment.teacher_name} on{' '}
                          {new Date(assignment.assigned_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <Badge 
                        variant="outline"
                        style={{ 
                          backgroundColor: videoCategories.find(c => c.name.toLowerCase() === assignment.video.category.toLowerCase())?.color + '20',
                          color: videoCategories.find(c => c.name.toLowerCase() === assignment.video.category.toLowerCase())?.color,
                          border: `1px solid ${videoCategories.find(c => c.name.toLowerCase() === assignment.video.category.toLowerCase())?.color}40`
                        }}
                      >
                        {assignment.video.category}
                      </Badge>
                    </div>
                  </CardHeader>
                  {assignment.notes && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{assignment.notes}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Assign Video to Teachers
            </DialogTitle>
            {selectedVideo && (
              <p className="text-sm text-muted-foreground mt-1">
                Video: {selectedVideo.title}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-6">
            {/* Teacher Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Select Teachers</Label>
                <Badge variant="secondary">
                  {assignmentData.teacher_ids.length} selected
                </Badge>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search teachers by name or email..."
                  value={teacherSearchTerm}
                  onChange={(e) => setTeacherSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Select All */}
              <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id="select-all"
                  checked={filteredTeachers.length > 0 && filteredTeachers.every(t => assignmentData.teacher_ids.includes(t.id))}
                  onCheckedChange={(checked) => toggleAllTeachers(checked as boolean)}
                />
                <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  Select All ({filteredTeachers.length} teachers)
                </Label>
              </div>

              {/* Teacher List */}
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {filteredTeachers.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No teachers found</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredTeachers.map((teacher) => (
                      <div key={teacher.id} className="flex items-center space-x-3 p-3 hover:bg-muted/50 transition-colors">
                        <Checkbox
                          id={`teacher-${teacher.id}`}
                          checked={assignmentData.teacher_ids.includes(teacher.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setAssignmentData({
                                ...assignmentData,
                                teacher_ids: [...assignmentData.teacher_ids, teacher.id]
                              });
                            } else {
                              setAssignmentData({
                                ...assignmentData,
                                teacher_ids: assignmentData.teacher_ids.filter(id => id !== teacher.id)
                              });
                            }
                          }}
                        />
                        <Label 
                          htmlFor={`teacher-${teacher.id}`} 
                          className="flex-1 cursor-pointer"
                        >
                          <div className="font-medium">
                            {teacher.first_name} {teacher.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {teacher.email}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Assignment Notes */}
            <div className="space-y-2">
              <Label htmlFor="assignment-notes" className="text-base font-semibold">
                Instructions / Notes (Optional)
              </Label>
              <Textarea
                id="assignment-notes"
                value={assignmentData.notes}
                onChange={(e) => setAssignmentData({...assignmentData, notes: e.target.value})}
                placeholder="Add instructions or notes for the teachers (e.g., 'Please watch this video and be prepared to discuss during our next meeting')"
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                This video will be added to each teacher's assignments
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAssignDialog(false);
                    setTeacherSearchTerm("");
                    setAssignmentData({ teacher_ids: [], notes: "" });
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAssignVideo}
                  disabled={assignmentData.teacher_ids.length === 0}
                  className="gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Assign to {assignmentData.teacher_ids.length} Teacher{assignmentData.teacher_ids.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Video to Course Dialog */}
      {selectedVideo && (
        <AddVideoToCourseDialog
          videoId={selectedVideo.id}
          videoTitle={selectedVideo.title}
          videoUrl={selectedVideo.video_url}
          open={showAddToCourseDialog}
          onOpenChange={setShowAddToCourseDialog}
        />
      )}
    </div>
  );
}
